import {
  Component,
  computed,
  inject,
  Input,
  input,
  InputSignal,
  model,
  ModelSignal,
  OnDestroy,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  TemplateRef,
  WritableSignal,
} from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import {
  ToggleElementDirective,
  TXPosition,
  TYPosition,
  TooltipDirective,
} from '@shared/directives';
import { ListItemComponent, TCheckboxConfigListItem } from '@shared/components/ui/list-item';
import { ScrollAreaComponent } from '@shared/components/ui/scroll-area';
import { InputComponent } from '@shared/components/ui/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ISystemIcons } from '@shared/components/ui/system-icons';

export type TDropdownListItem = {
  id?: number | string;
  value?: any;
  description?: string;
  disabled?: boolean;
  group?: string;
  rightIcon?: string;
  leftIcon?: string;
  systemLeftIcon?: ISystemIcons['iconName'];
  systemRightIcon?: ISystemIcons['iconName'];
  subItems?: TDropdownListItem[];
  displayTooltip?: boolean;
  tooltip?: string;
  checkbox?: TCheckboxConfigListItem;
  secondaryText?: string;
  hidden?: boolean;
  /** When true, shows a pin icon to pin/unpin the item */
  pinnable?: boolean;
  /** When true, indicates the item is currently pinned */
  pinned?: boolean;
  /** Called when the pin icon is clicked (only for items without subItems) */
  onPin?: (item: TDropdownListItem) => void;
};

export type TDropdownGroup = {
  items: TDropdownListItem[];
};

export type TSearchBoxConfig = {
  placeholder: string;
  icon?: string;
};

/**
 * Submenu position relative to parent item
 * @type {'right' | 'left'}
 */
export type TSubMenuPosition = 'right' | 'left';

/**
 * A reusable dropdown component with configurable options and selection handling
 *
 * @component
 * @selector lib-dropdown-list
 *
 * @example
 * <lib-dropdown-list
 *   [dataSource]="[{id: '1', description: 'Option 1'}, {id: '2', description: 'Option 2'}]"
 *   [isDisabled]="false"
 *   [selectedOption]="{id: '1', description: 'Option 1'}"
 *   (selectionChange)="onSelectionChange($event)">
 * </lib-dropdown-list>
 */
@Component({
  selector: 'lib-dropdown-list',
  imports: [
    CommonModule,
    NgClass,
    ToggleElementDirective,
    ListItemComponent,
    ScrollAreaComponent,
    InputComponent,
    TranslateModule,
    TooltipDirective,
  ],
  templateUrl: './dropdown-list.component.html',
  styleUrl: './dropdown-list.component.scss',
})
export class DropdownListComponent implements OnDestroy {
  private readonly translateService = inject(TranslateService);
  /**
   * Text used to filter the data source based on user input.
   *
   * @default ''
   */
  public readonly filterText: WritableSignal<string> = signal('');

  public readonly searchBoxConfig: InputSignal<TSearchBoxConfig> = input<
    TSearchBoxConfig>(null);

  /**
   * @param zIndexClass The class to apply the z-index.
   */
  public readonly zIndexDropdownClass: InputSignal<string> = input<string>();

  public readonly showSearchBox: InputSignal<boolean> = input<boolean>(false);

  /**
   * Array of dropdown items to display
   * @type {InputSignal<TDropdownListItem[]>}
   */
  public readonly dataSource: InputSignal<TDropdownListItem[]> = input<TDropdownListItem[]>([]);

  /**
   * Controls whether the dropdown is disabled
   * @type {InputSignal<boolean>}
   */
  public readonly isDisabled: InputSignal<boolean> = input(false);

  /**
   * Element ID for the dropdown container
   * @type {InputSignal<string>}
   */
  public readonly elementId: InputSignal<string> = input<string>('');

  /**
   * Reference to the anchor element for positioning
   * @type {InputSignal<HTMLElement>}
   */
  public readonly anchor: InputSignal<HTMLElement> = input<HTMLElement>();
  public readonly show: ModelSignal<boolean> = model(false);
  public readonly toggleByAnchor: InputSignal<boolean> = input<boolean>(true);
  /**
   * @param xAxis The x-axis position of the element.
   * @default 'start'
   */
  public xAxis: InputSignal<TXPosition> = input<TXPosition>('start');

  /**
   * @param yAxis The y-axis position of the element.
   * @default 'end'
   */
  public yAxis: InputSignal<TYPosition> = input<TYPosition>('end');

  /**
   * Distance between the dropdown and the anchor element
   * @type {InputSignal<number>}
   * @default 4
   */
  public distance: InputSignal<number> = input<number>(4);

  /**
   * Controla o lado para o qual o submenu se abre
   * @type {InputSignal<TSubMenuPosition>}
   */
  public readonly subMenuPosition: InputSignal<TSubMenuPosition> = input<TSubMenuPosition>('right');

  /**
   * Emits when a new option is selected
   * @type {OutputEmitterRef<TDropdownListItem>}
   */
  public readonly selectionChange: OutputEmitterRef<TDropdownListItem> = output<TDropdownListItem>();

  /**
   * Emits when the mouse enters the dropdown panel (useful when panel is in body and parent tracks hover).
   */
  public readonly panelMouseEnter: OutputEmitterRef<void> = output<void>();

  /**
   * Emits when the mouse leaves the dropdown panel.
   */
  public readonly panelMouseLeave: OutputEmitterRef<void> = output<void>();

  /**
   * Controls whether the dropdown allows selection changes
   * @type {InputSignal<boolean>}
   */
  public readonly allowSelectionChanges: InputSignal<boolean> = input(true);

  /**
   * The currently selected dropdown item
   * @type {TDropdownListItem | null}
   */
  @Input() selectedOption: TDropdownListItem | null = null;

  /**
   * Controls whether the dropdown items are grouped
   * @type {InputSignal<boolean>}
   */
  public readonly grouped: InputSignal<boolean> = input(false);

  /**
   * Controls whether the dropdown is scrollable
   * @type {InputSignal<boolean>}
   */
  public readonly scrollable: InputSignal<boolean> = input(true);

  /**
   * ID do item atualmente em hover que possui sub-itens
   * @type {WritableSignal<number | string | null>}
   */
  public hoveredItemWithSubItems: WritableSignal<TDropdownListItem> = signal(null);

  /**
   * Timeout para esconder o submenu
   * @private
   */
  private _hideTimeoutId: any = null;
  private readonly _hideDelay = 300; // 300ms de delay para esconder

  /**
   * CSS class for an optional icon displayed to the right of the text.
   * Typically uses an icon font like Font Awesome or a custom icon set.
   * @type {InputSignal<string>}
   * @default ''
   */
  public readonly displaySelectedIcon: InputSignal<boolean> = input<boolean>(true);

  /**
   * Controls whether the dropdown closes when clicking inside its container
   * @type {InputSignal<boolean>}
   * @default true
   */
  public readonly closeOnInternalClick: InputSignal<boolean> = input(true);

  /**
   * Controls whether the dropdown closes when an option is selected
   * @type {InputSignal<boolean>}
   * @default true
   */
  public readonly closeOnSelection: InputSignal<boolean> = input(true);

  /**
   * Optional per-item template. When set, each top-level option renders through
   * it (context: `$implicit`/`item` = the option, `selected`) instead of the
   * default list-item; the click/selection wrapper is preserved. Consumers that
   * don't set it keep the existing list-item rendering unchanged.
   */
  public readonly itemTemplate = input<TemplateRef<unknown> | null>(null);

  /**
   * Data source narrowed down by {@link filterText}.
   *
   * Derived (instead of a plain field written from an `effect`) so the array
   * identity only changes when the source or the filter actually changes —
   * a new identity on every change detection cycle makes `@for` re-reconcile
   * (destroy/recreate) the option views on every tick.
   */
  public readonly filteredDataSource: Signal<TDropdownListItem[]> = computed(() => {
    const dataSource = this.dataSource() ?? [];
    const filter = this.filterText().trim().toLowerCase();

    if (!filter) {
      return dataSource;
    }

    return dataSource.filter((item) => {
      const description = item.description
        ? this.translateService?.instant(item.description)
        : '';

      return description?.toLowerCase().includes(filter);
    });
  });

  /**
   * {@link filteredDataSource} arranged in groups, memoized for the same reason.
   */
  public readonly groupedDataSource: Signal<TDropdownGroup[]> = computed(() =>
    this.getGroupedItems(this.filteredDataSource())
  );

  /**
   * Handles selection of a dropdown option or sub-option
   * @param {TDropdownListItem} option - The selected dropdown item
   * @param {boolean} isSubItem - Indicates if the selected item is a sub-item
   * @returns {void}
   */
  public selectOption(option: TDropdownListItem, isSubItem = false): void {
    if (this.isDisabled() || option.disabled) return;

    if (option.subItems && option.subItems.length > 0 && !isSubItem) {
      return;
    }

    this.selectionChange.emit(option);
    if (this.allowSelectionChanges()) {
      this.selectedOption = option;
    }

    if (this.closeOnSelection()) {
      this.show.set(false);
      this.hoveredItemWithSubItems.set(null);
    }
  }

  /**
   * Returns the list of dropdown items grouped by group property
   */
  public getGroupedItems(items: TDropdownListItem[]): TDropdownGroup[] {
    if (!this.grouped() || !items.length) {
      return [{ items }];
    }

    const groupsMap = new Map<string, TDropdownListItem[]>();

    items.forEach((item) => {
      const groupKey = item.group ?? null;
      const groupItems = groupsMap.get(groupKey) ?? [];
      groupItems.push(item);
      groupsMap.set(groupKey, groupItems);
    });

    return Array.from(groupsMap.values()).map(items => ({ items }));
  }

  /**
   * Cancela o timer de esconder o submenu, se existir.
   * @private
   */
  private _cancelHideTimeout(): void {
    if (this._hideTimeoutId) {
      clearTimeout(this._hideTimeoutId);
      this._hideTimeoutId = null;
    }
  }

  /**
   * Inicia o timer para esconder o submenu após um delay.
   * @private
   */
  private _startHideTimeout(): void {
    this._cancelHideTimeout(); // Cancela qualquer timer anterior
    this._hideTimeoutId = setTimeout(() => {
      this.hoveredItemWithSubItems.set(null);
      this._hideTimeoutId = null;
    }, this._hideDelay);
  }

  /**
   * Chamado quando o mouse entra na área do item pai.
   * @param {TDropdownListItem} item - O item pai.
   */
  public handleMouseEnterItem(item: TDropdownListItem): void {
    this._cancelHideTimeout();

    if (item.subItems && item.subItems.length > 0 && !item.disabled && !this.isDisabled()) {
      this.hoveredItemWithSubItems.set(item);
    } else {
      this.hoveredItemWithSubItems.set(null);
    }
  }

  /**
   * Chamado quando o mouse sai da área do item pai.
   */
  public handleMouseLeaveItem(): void {
    this._startHideTimeout();
  }

  /**
   * Chamado quando o mouse entra na área do submenu.
   */
  public handleMouseEnterSubMenu(): void {
    this._cancelHideTimeout();
  }

  /**
   * Chamado quando o mouse sai da área do submenu.
   */
  public handleMouseLeaveSubMenu(): void {
    this._startHideTimeout();
  }

  /**
   * Limpa o timeout ao destruir o componente.
   */
  ngOnDestroy(): void {
    this._cancelHideTimeout();
  }

  /**
   * Track key for the option loops.
   *
   * `id` is optional on {@link TDropdownListItem}, so tracking by it alone
   * yields `undefined` for every id-less item — duplicated keys, which Angular
   * only reports in dev mode (NG0955) and silently mis-reconciles in
   * production. Falling back to the index keeps the keys unique, and the
   * prefixes stop a numeric `id` from colliding with an index.
   */
  public trackOption(option: TDropdownListItem, index: number): string {
    return option?.id != null ? `id:${option.id}` : `idx:${index}`;
  }
}
